import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import CustomerTableHeader from "./CustomerTableHeader";
import CustomerRow from "./Rows/CustomerRow";
import {
  EDIT_COLUMNS,
  VIEW_COLUMNS,
} from "../../../config/customerColumnsConfig";
import { useManagementCustomers } from "../../../hooks/useManagementCustomers";
import { updateCustomerInState } from "../../../../../../../redux/mgmtCo/managementCustomerSlice";
import {
  setSelectedCustomer,
  clearSelectedCustomer,
  selectedSelectedCustomer,
} from "../../../../../../../redux/mgmtCo/selectedCustomerSlice";
import useAutoSaveCustomer from "../../../hooks/useAutoSaveCustomer";

const ManagementCustomerTable = ({
  managementID,
  isEditMode,
  newCustomerId,
  setNewCustomerId,
  onAddNew,
}) => {
  const dispatch = useDispatch();
  const selectedCustomer = useSelector(selectedSelectedCustomer);

  const {
    assignedCustomers,
    customerOptions,
    loadAssignedCustomers,
    loadCustomerOptionsWithManagementNull,
    addCustomerToManagement,
    removeCustomerFromManagement,
    updateCustomerData,
  } = useManagementCustomers(managementID);

  const [showAllCustomers, setShowAllCustomers] = useState(false);

  // Load assigned customers whenever the managementID changes.
  useEffect(() => {
    if (managementID) {
      loadAssignedCustomers();
    }
  }, [managementID, loadAssignedCustomers]);

  // Load customer options on mount.
  useEffect(() => {
    loadCustomerOptionsWithManagementNull();
  }, [assignedCustomers, loadCustomerOptionsWithManagementNull]);

  // Extract auto-save logic into its own hook.
  const autoSaveCustomer = useAutoSaveCustomer(
    assignedCustomers,
    updateCustomerData
  );

  // Handler for field changes in a customer row.
  const handleCustomerFieldChange = useCallback(
    (e, index) => {
      const { name, value } = e.target;
      const customer = assignedCustomers[index];
      const updatedCustomer = { ...customer, [name]: value };
      dispatch(updateCustomerInState(updatedCustomer));
    },
    [assignedCustomers, dispatch]
  );

  // Compute the displayed customers using useMemo.
  const displayedCustomers = useMemo(() => {
    if (!assignedCustomers || assignedCustomers.length === 0) return [];
    return showAllCustomers
      ? assignedCustomers
      : assignedCustomers.slice(0, 10);
  }, [assignedCustomers, showAllCustomers]);

  // Toggle row selection.
  const handleRowClick = useCallback(
    (customer) => {
      if (
        selectedCustomer &&
        selectedCustomer.customerID === customer.customerID
      ) {
        dispatch(clearSelectedCustomer());
      } else {
        dispatch(setSelectedCustomer(customer));
      }
    },
    [selectedCustomer, dispatch]
  );

  if (!assignedCustomers) {
    return (
      <div className="mb-4">
        <p>Loading customers...</p>
      </div>
    );
  }

  const editHandlers = {
    handleCustomerFieldChange,
    removeCustomer: removeCustomerFromManagement,
  };

  return (
    <div className="mb-4">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="sticky top-0 z-10">
            <CustomerTableHeader isEditMode={isEditMode} onAddNew={onAddNew} />
          </thead>
          <tbody>
            {isEditMode && (
              <CustomerRow
                mode="add"
                customerOptions={customerOptions}
                addCustomer={addCustomerToManagement}
                newCustomerId={newCustomerId}
                setNewCustomerId={setNewCustomerId}
              />
            )}
            {isEditMode
              ? displayedCustomers.map((customer, index) => (
                  <CustomerRow
                    key={customer.customerID}
                    mode="edit"
                    customer={customer}
                    index={index}
                    selectedCustomer={selectedCustomer}
                    onRowClick={handleRowClick}
                    handlers={editHandlers}
                    handleAutoSave={() => autoSaveCustomer(index)}
                  />
                ))
              : displayedCustomers.map((customer) => (
                  <CustomerRow
                    key={customer.customerID}
                    mode="view"
                    customer={customer}
                    selectedCustomer={selectedCustomer}
                    onRowClick={handleRowClick}
                  />
                ))}
            {assignedCustomers.length > 10 && !showAllCustomers && (
              <tr>
                <td
                  colSpan={
                    isEditMode ? EDIT_COLUMNS.length + 1 : VIEW_COLUMNS.length
                  }
                  className="text-center py-1"
                >
                  <button
                    onClick={() => setShowAllCustomers(true)}
                    className="px-1 py-1 border bg-sss-blue text-white font-semibold hover:bg-sss-blue-sel transition duration-150"
                  >
                    Show more
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

ManagementCustomerTable.propTypes = {
  managementID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  isEditMode: PropTypes.bool.isRequired,
  newCustomerId: PropTypes.string,
  setNewCustomerId: PropTypes.func.isRequired,
};

export default ManagementCustomerTable;
